/**
 * MarketingAssets — landlord studio to generate a printable flyer,
 * social square, or listing text from a unit's data.
 * Route: /marketing-assets (subscriber-only).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Download, Copy, Loader2, ArrowRight, FileText, Instagram, MessageSquare, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui/Primitives";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";

const KIND_ICON = { flyer: FileText, social: Instagram, text: MessageSquare, email: Mail };

export default function MarketingAssets() {
  usePageTitle("Marketing Assets — Central Rentals Canada");
  const [params] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [units, setUnits] = useState([]);
  const [templateSlug, setTemplateSlug] = useState("flyer_a4");
  const [unitId, setUnitId] = useState("");
  const [headline, setHeadline] = useState("");
  const [highlightsText, setHighlightsText] = useState("");
  const [rendering, setRendering] = useState(false);
  const [rendered, setRendered] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paywalled, setPaywalled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, u] = await Promise.all([
          api.get("/marketing-assets/templates"),
          api.get("/units").catch(() => ({ data: [] })),
        ]);
        setTemplates(t.data?.templates || []);
        setUnits(Array.isArray(u.data) ? u.data : (u.data?.units || []));
        const initialUnit = params.get("unit") || (Array.isArray(u.data) && u.data[0]?.unit_id) || "";
        setUnitId(initialUnit);
      } catch (e) {
        if (e?.response?.status === 402) setPaywalled(true);
        else toast.error("Couldn't load Marketing Assets studio.");
      } finally {
        setLoading(false);
      }
    })();
  }, [params]);

  const activeTemplate = useMemo(
    () => templates.find((t) => t.slug === templateSlug),
    [templates, templateSlug]
  );

  const render = async () => {
    if (!unitId) { toast.error("Pick a unit first."); return; }
    setRendering(true);
    setRendered(null);
    try {
      const highlights = highlightsText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 5);
      const { data } = await api.post("/marketing-assets/render", {
        template_slug: templateSlug,
        unit_id: unitId,
        headline: headline.trim() || null,
        highlights: highlights.length ? highlights : null,
      });
      setRendered(data);
    } catch (e) {
      if (e?.response?.status === 402) setPaywalled(true);
      else toast.error(e?.response?.data?.detail || "Couldn't render this asset.");
    } finally {
      setRendering(false);
    }
  };

  const download = () => {
    if (!rendered) return;
    if (rendered.html) {
      const blob = new Blob([rendered.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${templateSlug}-${unitId.slice(0, 8)}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Downloaded. Open the file and use Print → Save as PDF.");
    } else if (rendered.text) {
      const blob = new Blob([rendered.text], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${templateSlug}-${unitId.slice(0, 8)}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Text file downloaded.");
    }
  };

  const openPrintTab = () => {
    if (!rendered?.html) return;
    const w = window.open("about:blank", "_blank");
    if (!w) { toast.error("Popup blocked — allow pop-ups and try again."); return; }
    w.document.open();
    w.document.write(rendered.html);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 400);
  };

  const copyText = async () => {
    if (!rendered?.text) return;
    try {
      await navigator.clipboard.writeText(rendered.text);
      toast.success("Copied to clipboard.");
    } catch { toast.error("Couldn't copy — select the text manually."); }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-5 py-10 flex items-center text-slate-500" data-testid="marketing-assets-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading studio…
      </div>
    );
  }
  if (paywalled) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-12" data-testid="marketing-assets-paywall">
        <Card className="p-8 bg-amber-50 border-amber-200">
          <div className="text-lg font-bold text-amber-900">Marketing Assets is a subscriber-only feature</div>
          <p className="mt-2 text-sm text-amber-800">Generate branded flyers, social posts, and listing text for every unit. Available on every paid plan.</p>
          <Link to="/billing" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold text-sm">Open Billing <ArrowRight className="w-4 h-4"/></Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 py-8" data-testid="marketing-assets-page">
      <PageHeader
        title="Marketing Assets"
        subtitle="Generate a printable flyer, social square, or listing text — plain-English wording, ready to print or paste."
        icon={Sparkles}
      />

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <Card className="p-5" data-testid="marketing-assets-controls">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">1. Pick a template</div>
            <div className="grid grid-cols-2 gap-2" data-testid="marketing-assets-templates">
              {templates.map((t) => {
                const Icon = KIND_ICON[t.kind] || FileText;
                const active = t.slug === templateSlug;
                return (
                  <button
                    key={t.slug}
                    type="button"
                    onClick={() => setTemplateSlug(t.slug)}
                    data-testid={`marketing-template-${t.slug}`}
                    className={`text-left p-3 rounded-lg border transition-colors ${active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  >
                    <Icon className={`w-4 h-4 ${active ? "text-emerald-700" : "text-slate-500"}`} />
                    <div className={`mt-1.5 font-bold text-xs ${active ? "text-emerald-900" : "text-slate-900"}`}>{t.name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t.size}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">2. Pick a unit</div>
            <select
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              data-testid="marketing-unit-select"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
            >
              <option value="">Choose a unit…</option>
              {units.map((u) => {
                const subtitle = u.property_name || u.address || "";
                const label = subtitle ? `${u.label || "Unit"} — ${subtitle}` : (u.label || "Unit");
                return (
                  <option key={u.unit_id} value={u.unit_id}>{label}</option>
                );
              })}
            </select>
            {units.length === 0 && (
              <p className="mt-2 text-xs text-slate-500">No units yet. <Link to="/properties" className="underline text-emerald-700">Add one first</Link>.</p>
            )}

            <div className="mt-5 text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">3. Optional — customise</div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Headline</label>
            <input
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="Leave blank for auto-generated"
              data-testid="marketing-headline-input"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-3"
            />
            <label className="block text-xs font-semibold text-slate-600 mb-1">Highlights (one per line, max 5)</label>
            <textarea
              value={highlightsText}
              onChange={(e) => setHighlightsText(e.target.value)}
              rows={5}
              placeholder="Leave blank to use smart defaults"
              data-testid="marketing-highlights-input"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none"
            />

            <button
              type="button"
              onClick={render}
              disabled={rendering || !unitId}
              data-testid="marketing-render-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm"
            >
              {rendering ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating…</> : <><Sparkles className="w-4 h-4"/> Generate asset</>}
            </button>
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card className="p-5 min-h-[420px]" data-testid="marketing-assets-preview">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold text-slate-900">Preview</h3>
              {rendered && (
                <div className="flex items-center gap-2">
                  {rendered.text && (
                    <button type="button" onClick={copyText} data-testid="marketing-copy-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 text-xs font-semibold text-slate-700">
                      <Copy className="w-3.5 h-3.5"/> Copy
                    </button>
                  )}
                  {rendered.html && (
                    <button type="button" onClick={openPrintTab} data-testid="marketing-print-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 text-xs font-semibold text-slate-700">
                      Open in new tab
                    </button>
                  )}
                  <button type="button" onClick={download} data-testid="marketing-download-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold">
                    <Download className="w-3.5 h-3.5"/> Download
                  </button>
                </div>
              )}
            </div>
            {!rendered && (
              <div className="text-sm text-slate-500 text-center py-16" data-testid="marketing-preview-empty">
                {activeTemplate?.description || "Pick a template and unit, then hit Generate."}
              </div>
            )}
            {rendered?.html && (
              <iframe
                title="Asset preview"
                data-testid="marketing-preview-iframe"
                srcDoc={rendered.html}
                className="w-full h-[520px] rounded-lg border border-slate-200 bg-white"
              />
            )}
            {rendered?.text && (
              <pre data-testid="marketing-preview-text" className="whitespace-pre-wrap font-mono text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-4 leading-relaxed">
                {rendered.text}
              </pre>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
